export const LIGHT_THEME_CSS = `
  [data-theme="light"], :root {
    --paper: #FAF9F5;
    --paper-2: #F3F2EC;
    --card: #FFFFFF;
    --card-2: #F8F7F3;
    --surface: #FFFFFF;
    --border: #E4E3DB;
    --border-2: #D5D4CB;
    --ink: #1D1E27;
    --ink-2: #5A5B68;
    --ink-3: #8E8F9C;
    --ink-inv: #FFFFFF;
    --accent: #2D5CF6;
    --accent-2: #1A46D9;
    --accent-soft: #EEF2FF;
    --success: #1F8A5B;
    --success-soft: #E8F6EF;
    --warn: #B7791F;
    --warn-soft: #FEF3E2;
    --danger: #C2452E;
    --danger-soft: #FDECEA;
    --info: #1C71B8;
    --info-soft: #E8F3FC;
    --neutral-soft: #F0EFEA;
    --overlay: rgba(29,30,39,0.5);
    --shadow-sm: 0 1px 3px rgba(29,30,39,0.08);
    --shadow-md: 0 4px 12px rgba(29,30,39,0.10);
    --shadow-lg: 0 8px 24px rgba(29,30,39,0.12);
    --r-1: 3px;
    --r-2: 5px;
    --r-3: 8px;
    --r-4: 12px;
    --r-5: 16px;
    --r-full: 9999px;
    --fs-xs: 11px;
    --fs-sm: 12px;
    --fs-base: 13px;
    --fs-md: 14px;
    --fs-lg: 16px;
    --fs-xl: 18px;
    --fs-2xl: 22px;
    --fs-3xl: 28px;
    --fw-regular: 400;
    --fw-medium: 500;
    --fw-semibold: 600;
    --font-sans: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    --font-mono: 'IBM Plex Mono', 'Fira Mono', monospace;
    --nav-w: 220px;
    --topbar-h: 52px;
  }
`;

export const DARK_THEME_CSS = `
  [data-theme="dark"] {
    --paper: #1A1B23;
    --paper-2: #14151C;
    --card: #272838;
    --card-2: #2F3042;
    --surface: #272838;
    --border: #383952;
    --border-2: #44466A;
    --ink: #F5F4F0;
    --ink-2: #B0B1C4;
    --ink-3: #6E6F84;
    --ink-inv: #1D1E27;
    --accent: #7B9FFF;
    --accent-2: #9BB5FF;
    --accent-soft: #1E2845;
    --success: #3DBD82;
    --success-soft: #0E2E1E;
    --warn: #F0A830;
    --warn-soft: #2E1F06;
    --danger: #F4694F;
    --danger-soft: #2E0F0A;
    --info: #5BAEE8;
    --info-soft: #0C1E30;
    --neutral-soft: #24253A;
    --overlay: rgba(0,0,0,0.6);
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.3);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.4);
    --shadow-lg: 0 8px 24px rgba(0,0,0,0.5);
  }
`;

export const BASE_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--paper); color: var(--ink); font-family: var(--font-sans); font-size: var(--fs-base); line-height: 1.5; }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border-2); border-radius: var(--r-full); }

  /* Buttons */
  .t-btn { display: inline-flex; align-items: center; gap: 6px; padding: 0 14px; height: 34px; border-radius: var(--r-3); font-size: var(--fs-base); font-weight: var(--fw-medium); font-family: var(--font-sans); cursor: pointer; border: 1.5px solid transparent; transition: background 0.15s, color 0.15s, border-color 0.15s, opacity 0.15s; text-decoration: none; white-space: nowrap; }
  .t-btn:disabled, .t-btn[disabled] { opacity: 0.45; cursor: not-allowed; }
  .t-btn.accent { background: var(--accent); color: var(--ink-inv); }
  .t-btn.accent:hover:not(:disabled) { background: var(--accent-2); }
  .t-btn.ghost { background: transparent; color: var(--ink-2); }
  .t-btn.ghost:hover:not(:disabled) { background: var(--neutral-soft); color: var(--ink); }
  .t-btn.outline { background: transparent; color: var(--ink); border-color: var(--border-2); }
  .t-btn.outline:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
  .t-btn.danger { background: var(--danger-soft); color: var(--danger); }
  .t-btn.danger:hover:not(:disabled) { background: var(--danger); color: white; }
  .t-btn.success { background: var(--success-soft); color: var(--success); }
  .t-btn.success:hover:not(:disabled) { background: var(--success); color: white; }
  .t-btn.sm { height: 28px; padding: 0 10px; font-size: var(--fs-sm); border-radius: var(--r-2); }
  .t-btn.xs { height: 24px; padding: 0 8px; font-size: var(--fs-xs); border-radius: var(--r-2); }
  .t-btn.lg { height: 40px; padding: 0 20px; font-size: var(--fs-md); }
  .t-btn.block { width: 100%; justify-content: center; }
  .t-btn.icon { padding: 0; width: 34px; justify-content: center; }
  .t-btn.icon.sm { width: 28px; }

  /* Chips */
  .t-chip { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: var(--r-full); font-size: var(--fs-xs); font-weight: var(--fw-medium); white-space: nowrap; }
  .t-chip.accent { background: var(--accent-soft); color: var(--accent); }
  .t-chip.success { background: var(--success-soft); color: var(--success); }
  .t-chip.warn { background: var(--warn-soft); color: var(--warn); }
  .t-chip.danger { background: var(--danger-soft); color: var(--danger); }
  .t-chip.info { background: var(--info-soft); color: var(--info); }
  .t-chip.neutral { background: var(--neutral-soft); color: var(--ink-2); }
  .t-chip.lg { padding: 4px 10px; font-size: var(--fs-sm); border-radius: var(--r-2); }
  .t-chip .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0; }

  /* Cards */
  .t-card { background: var(--card); border: 1px solid var(--border); border-radius: var(--r-4); }
  .t-card .card-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 0; }
  .t-card .card-body { padding: 14px 16px; }
  .t-card .card-foot { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--border); }
  .t-card h3 { font-size: var(--fs-md); font-weight: var(--fw-semibold); color: var(--ink); }

  /* Avatar */
  .t-avatar { display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; font-weight: var(--fw-semibold); font-family: var(--font-sans); flex-shrink: 0; }
  .t-avatar.xs { width: 24px; height: 24px; font-size: 9px; }
  .t-avatar.sm { width: 32px; height: 32px; font-size: 11px; }
  .t-avatar { width: 36px; height: 36px; font-size: 13px; }
  .t-avatar.lg { width: 44px; height: 44px; font-size: 15px; }
  .t-avatar.xl { width: 56px; height: 56px; font-size: 18px; }

  /* Input */
  .t-input { display: block; width: 100%; padding: 0 10px; height: 34px; background: var(--card); border: 1.5px solid var(--border-2); border-radius: var(--r-3); font-size: var(--fs-base); font-family: var(--font-sans); color: var(--ink); outline: none; transition: border-color 0.15s; }
  .t-input:focus { border-color: var(--accent); }
  .t-input.sm { height: 28px; font-size: var(--fs-sm); }
  .t-input::placeholder { color: var(--ink-3); }
  select.t-input { cursor: pointer; }

  /* Tabs */
  .t-tabs { display: flex; gap: 2px; border-bottom: 1.5px solid var(--border); margin-bottom: 16px; }
  .t-tab { padding: 8px 14px; font-size: var(--fs-base); font-weight: var(--fw-medium); color: var(--ink-2); cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1.5px; white-space: nowrap; transition: color 0.15s, border-color 0.15s; background: none; border-top: none; border-left: none; border-right: none; font-family: var(--font-sans); }
  .t-tab.active { color: var(--accent); border-bottom-color: var(--accent); }
  .t-tab:hover:not(.active) { color: var(--ink); }

  /* Table */
  .t-table { width: 100%; border-collapse: collapse; font-size: var(--fs-base); }
  .t-table th { text-align: left; padding: 8px 12px; font-size: var(--fs-xs); font-weight: var(--fw-semibold); color: var(--ink-3); text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1.5px solid var(--border); white-space: nowrap; }
  .t-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); color: var(--ink); vertical-align: middle; }
  .t-table tr:last-child td { border-bottom: none; }
  .t-table tr:hover td { background: var(--paper-2); }
  .t-table.compact td, .t-table.compact th { padding: 6px 12px; }

  /* Modal */
  .t-modal-backdrop { position: fixed; inset: 0; background: var(--overlay); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .t-modal { background: var(--card); border-radius: var(--r-5); box-shadow: var(--shadow-lg); width: 100%; max-width: 580px; max-height: 92vh; overflow: visible; display: flex; flex-direction: column; }
  .t-modal.wide { max-width: 720px; }
  .t-modal.full { max-width: 960px; }
  .t-modal .modal-head { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0; border-radius: var(--r-5) var(--r-5) 0 0; background: var(--card); }
  .t-modal .modal-head h2 { font-size: var(--fs-lg); font-weight: var(--fw-semibold); color: var(--ink); }
  .t-modal .modal-body { padding: 24px; overflow-y: auto; flex: 1; overflow-x: visible; }
  .t-modal .modal-foot { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 16px 24px; border-top: 1px solid var(--border); flex-shrink: 0; border-radius: 0 0 var(--r-5) var(--r-5); background: var(--card); }

  /* Drawer */
  .t-drawer-backdrop { position: fixed; inset: 0; background: var(--overlay); z-index: 900; }
  .t-drawer { position: fixed; top: 0; right: 0; height: 100%; background: var(--card); box-shadow: var(--shadow-lg); z-index: 901; display: flex; flex-direction: column; transition: width 0.25s; }

  /* Toast */
  .t-toast-host { position: fixed; bottom: 24px; right: 24px; z-index: 9999; display: flex; flex-direction: column; gap: 8px; pointer-events: none; }
  .t-toast { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: var(--card); border: 1px solid var(--border); border-radius: var(--r-4); box-shadow: var(--shadow-md); font-size: var(--fs-base); color: var(--ink); pointer-events: all; min-width: 240px; max-width: 360px; }
  .t-toast.success { border-left: 3px solid var(--success); }
  .t-toast.warn { border-left: 3px solid var(--warn); }
  .t-toast.danger { border-left: 3px solid var(--danger); }
  .t-toast.info { border-left: 3px solid var(--info); }

  /* Alert */
  .t-alert { display: flex; gap: 10px; padding: 12px 14px; border-radius: var(--r-3); font-size: var(--fs-base); }
  .t-alert.info { background: var(--info-soft); color: var(--info); }
  .t-alert.success { background: var(--success-soft); color: var(--success); }
  .t-alert.warn { background: var(--warn-soft); color: var(--warn); }
  .t-alert.danger { background: var(--danger-soft); color: var(--danger); }

  /* Empty state */
  .t-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 48px 24px; color: var(--ink-3); text-align: center; }
  .t-empty .empty-icon { opacity: 0.35; }
  .t-empty .empty-title { font-size: var(--fs-md); font-weight: var(--fw-medium); color: var(--ink-2); }
  .t-empty .empty-desc { font-size: var(--fs-sm); }

  /* Switch */
  .t-switch { position: relative; display: inline-flex; align-items: center; cursor: pointer; }
  .t-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
  .t-switch .track { width: 34px; height: 18px; background: var(--border-2); border-radius: var(--r-full); transition: background 0.2s; position: relative; }
  .t-switch input:checked + .track { background: var(--accent); }
  .t-switch .track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; background: white; border-radius: 50%; transition: transform 0.2s; }
  .t-switch input:checked + .track::after { transform: translateX(16px); }

  /* Skeleton */
  .t-skeleton { background: linear-gradient(90deg, var(--border) 25%, var(--border-2) 50%, var(--border) 75%); background-size: 200% 100%; animation: skeleton-shimmer 1.5s infinite; border-radius: var(--r-2); }
  @keyframes skeleton-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  /* Spinner */
  .t-spinner { width: 18px; height: 18px; border: 2px solid var(--border-2); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Nav */
  .sk-nav { width: var(--nav-w); background: var(--card); border-right: 1px solid var(--border); display: flex; flex-direction: column; height: 100vh; position: fixed; left: 0; top: 0; z-index: 100; flex-shrink: 0; }
  .sk-nav-brand { display: flex; align-items: center; gap: 10px; padding: 0 16px; height: var(--topbar-h); border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .sk-nav-brand .brand-name { font-size: var(--fs-md); font-weight: var(--fw-semibold); color: var(--ink); }
  .sk-nav-body { flex: 1; overflow-y: auto; padding: 8px 0; }
  .sk-nav-section { padding: 8px 12px 4px; font-size: var(--fs-xs); font-weight: var(--fw-semibold); color: var(--ink-3); text-transform: uppercase; letter-spacing: 0.06em; }
  .sk-nav-item { display: flex; align-items: center; gap: 10px; padding: 7px 12px; margin: 1px 8px; border-radius: var(--r-3); font-size: var(--fs-base); color: var(--ink-2); cursor: pointer; transition: background 0.12s, color 0.12s; text-decoration: none; }
  .sk-nav-item:hover { background: var(--paper-2); color: var(--ink); }
  .sk-nav-item.active { background: var(--accent-soft); color: var(--accent); font-weight: var(--fw-medium); }
  .sk-nav-foot { padding: 8px; border-top: 1px solid var(--border); flex-shrink: 0; }

  /* Role segmented control */
  .sk-roleseg { display: inline-flex; background: var(--paper-2); border-radius: var(--r-3); padding: 3px; gap: 2px; }
  .sk-roleseg button { padding: 4px 10px; border-radius: var(--r-2); font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--ink-2); background: none; border: none; cursor: pointer; font-family: var(--font-sans); transition: background 0.12s, color 0.12s; }
  .sk-roleseg button.active { background: var(--card); color: var(--ink); box-shadow: var(--shadow-sm); }

  /* Main layout */
  .sk-shell { display: flex; height: 100vh; background: var(--paper); }
  .sk-main { flex: 1; margin-left: var(--nav-w); display: flex; flex-direction: column; min-height: 100vh; min-width: 0; }
  .sk-topbar { display: flex; align-items: center; justify-content: space-between; padding: 0 24px; height: var(--topbar-h); background: var(--card); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 50; flex-shrink: 0; }
  .sk-content { flex: 1; padding: 24px; overflow-y: auto; }
  .sk-page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .sk-page-header h1 { font-size: var(--fs-2xl); font-weight: var(--fw-semibold); color: var(--ink); }

  /* Stat card */
  .sk-stat { background: var(--card); border: 1px solid var(--border); border-radius: var(--r-4); padding: 16px; }
  .sk-stat .stat-label { font-size: var(--fs-xs); font-weight: var(--fw-semibold); color: var(--ink-3); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
  .sk-stat .stat-value { font-size: var(--fs-2xl); font-weight: var(--fw-semibold); color: var(--ink); line-height: 1.2; }
  .sk-stat .stat-sub { font-size: var(--fs-sm); color: var(--ink-3); margin-top: 4px; }

  /* Emp card */
  .sk-emp-card { background: var(--card); border: 1px solid var(--border); border-radius: var(--r-4); padding: 16px; cursor: pointer; transition: box-shadow 0.15s, border-color 0.15s; }
  .sk-emp-card:hover { box-shadow: var(--shadow-sm); border-color: var(--border-2); }
  .sk-emp-card .emp-name { font-size: var(--fs-md); font-weight: var(--fw-semibold); color: var(--ink); margin-top: 10px; }
  .sk-emp-card .emp-role { font-size: var(--fs-sm); color: var(--ink-2); }
  .sk-emp-card .emp-dept { font-size: var(--fs-xs); color: var(--ink-3); margin-top: 4px; }

  /* List row */
  .sk-listrow { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid var(--border); transition: background 0.1s; }
  .sk-listrow:last-child { border-bottom: none; }
  .sk-listrow:hover { background: var(--paper-2); }
  .sk-listrow .row-main { flex: 1; min-width: 0; }
  .sk-listrow .row-title { font-size: var(--fs-base); font-weight: var(--fw-medium); color: var(--ink); }
  .sk-listrow .row-sub { font-size: var(--fs-sm); color: var(--ink-2); margin-top: 2px; }
  .sk-listrow .row-meta { font-size: var(--fs-xs); color: var(--ink-3); margin-top: 2px; }

  /* Progress bar */
  .sk-bar { width: 100%; height: 6px; background: var(--border); border-radius: var(--r-full); overflow: hidden; }
  .sk-bar .bar-fill { height: 100%; border-radius: var(--r-full); transition: width 0.3s; }
  .sk-bar .bar-fill.accent { background: var(--accent); }
  .sk-bar .bar-fill.success { background: var(--success); }
  .sk-bar .bar-fill.warn { background: var(--warn); }
  .sk-bar .bar-fill.danger { background: var(--danger); }

  /* Copilot panel */
  .sk-copilot { width: 320px; background: var(--card); border-left: 1px solid var(--border); display: flex; flex-direction: column; height: 100vh; position: fixed; right: 0; top: 0; z-index: 90; }
  .sk-copilot-head { display: flex; align-items: center; justify-content: space-between; padding: 0 16px; height: var(--topbar-h); border-bottom: 1px solid var(--border); }
  .sk-copilot-body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
  .sk-copilot-msg { padding: 10px 12px; border-radius: var(--r-3); font-size: var(--fs-sm); line-height: 1.5; }
  .sk-copilot-msg.assistant { background: var(--accent-soft); color: var(--ink); }
  .sk-copilot-msg.user { background: var(--neutral-soft); color: var(--ink); align-self: flex-end; }
  .sk-copilot-foot { padding: 12px; border-top: 1px solid var(--border); display: flex; gap: 8px; }

  /* Form helpers */
  .form-row { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
  .form-row label { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--ink-2); }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }

  /* Grid helpers */
  .stats-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; margin-bottom: 20px; }
  .emp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(248px, 1fr)); gap: 12px; }
  .two-col { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: start; }

  /* Misc */
  .section-label { font-size: var(--fs-xs); font-weight: var(--fw-semibold); color: var(--ink-3); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
  .divider { height: 1px; background: var(--border); margin: 16px 0; }
  .mono { font-family: var(--font-mono); }
  .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  /* Workflow builder */
  .wf-shell { display: grid; grid-template-columns: 236px 1fr; height: 100%; gap: 0; }
  .wf-sidebar { border-right: 1px solid var(--border); overflow-y: auto; padding: 12px 0; }
  .wf-canvas { padding: 20px; overflow-y: auto; }
  .wf-step-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--r-4); padding: 14px 16px; margin-bottom: 10px; position: relative; }
  .wf-step-card.blocking { border-left: 3px solid var(--warn); }
  .wf-step-connector { width: 2px; height: 16px; background: var(--border-2); margin: 0 auto 0 28px; }

  /* Leave balance card */
  .leave-bal-card { background: var(--card); border: 1px solid var(--border); border-radius: var(--r-4); padding: 14px 16px; }
  .leave-bal-name { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--ink-2); margin-bottom: 6px; }
  .leave-bal-count { font-size: var(--fs-2xl); font-weight: var(--fw-semibold); color: var(--ink); line-height: 1; }
  .leave-bal-days { font-size: var(--fs-xs); color: var(--ink-3); margin-top: 2px; margin-bottom: 10px; }
`;

export const Colors = {
  paper: 'var(--paper)',
  paper2: 'var(--paper-2)',
  card: 'var(--card)',
  border: 'var(--border)',
  border2: 'var(--border-2)',
  ink: 'var(--ink)',
  ink2: 'var(--ink-2)',
  ink3: 'var(--ink-3)',
  inkInv: 'var(--ink-inv)',
  accent: 'var(--accent)',
  accentSoft: 'var(--accent-soft)',
  success: 'var(--success)',
  successSoft: 'var(--success-soft)',
  warn: 'var(--warn)',
  warnSoft: 'var(--warn-soft)',
  danger: 'var(--danger)',
  dangerSoft: 'var(--danger-soft)',
  info: 'var(--info)',
  infoSoft: 'var(--info-soft)',
  neutralSoft: 'var(--neutral-soft)',
  // Avatar palette (static, not theme-dependent)
  avatar: ['#4F46E5','#0891B2','#059669','#D97706','#DC2626','#7C3AED','#DB2777','#2563EB','#16A34A','#EA580C'],
};

export const Radii = {
  r1: 3, r2: 5, r3: 8, r4: 12, r5: 16, full: 9999,
};
